import React, { useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import CalendarIcon from '../../assets/icons/calendarIcon';
import TimeIcon from '../../assets/icons/timeIcon';
import { DocumentService } from '../../services/DocumentService';
import ConfirmationModal from '../modals/confirmationModal';
import RenameDocumentModal from '../modals/renameDocumentModal';
import { Toast } from './Toast';
import DeleteIcon from '../../assets/icons/delete.svg';
import Tooltip from './Tooltip';
import { getStatusMenuOptions, getStatusTooltip, getStatusStyleKey, toDisplayStatus } from '../../utils/statusTransitions';
import { getDocumentCardDescription } from '../../utils/documentCardDescription';
import { getDocumentTypeDisplayName } from '../../utils/documentTypeDescriptions';

const STATUS_STYLES = {
	Draft: 'bg-[#F2F3F7] text-dark-blue border border-[#F2F3F7]',
	Review: 'bg-[#FFF4E6] text-textOrange border border-[#FFF4E6]',
	'In Review': 'bg-[#FFF4E6] text-textOrange border border-[#FFF4E6]',
	Final: 'bg-[#E0FFED] text-textGreen border border-[#E0FFED]',
	Approved: 'bg-[#E0FFED] text-textGreen border border-[#E0FFED]',
	Submitted: 'bg-[#EDF6FF] text-textBlue border border-[#EDF6FF]'
};

const PILL =
	// sm: default (mobile); md/lg/xl scale up padding & text as breakpoints increase
	'text-xs font-medium leading-100 px-2 py-2.5 rounded-lg inline-flex items-center gap-2.5 ';

/** Small hook: close on outside click */
function useOnClickOutside(ref, handler) {
	useEffect(() => {
		const listener = (e) => {
			if (!ref.current || ref.current.contains(e.target)) return;
			handler();
		};
		document.addEventListener('mousedown', listener);
		document.addEventListener('touchstart', listener);
		return () => {
			document.removeEventListener('mousedown', listener);
			document.removeEventListener('touchstart', listener);
		};
	}, [ref, handler]);
}

const StatusIcon = ({ status }) => {
	const key = getStatusStyleKey(status);
	switch (key) {
		case 'Draft':
			return (
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
					<circle cx="7" cy="7" r="6.5" stroke="#180A63" strokeDasharray="3 3" />
				</svg>
			);
		case 'Review':
			return (
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
					<circle cx="7" cy="7" r="6.5" stroke="#EF7E22" />
					<path
						d="M7 12C7.65661 12 8.30679 11.8707 8.91342 11.6194C9.52004 11.3681 10.0712 10.9998 10.5355 10.5355C10.9998 10.0712 11.3681 9.52005 11.6194 8.91342C11.8707 8.30679 12 7.65661 12 7C12 6.34339 11.8707 5.69321 11.6194 5.08658C11.3681 4.47995 10.9998 3.92876 10.5355 3.46447C10.0712 3.00017 9.52004 2.63188 8.91342 2.3806C8.30679 2.12933 7.65661 2 7 2V7V12Z"
						fill="#EF7E22"
					/>
				</svg>
			);
		case 'Approved':
			return (
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M0 7C0 5.14348 0.737498 3.36301 2.05025 2.05025C3.36301 0.737498 5.14348 0 7 0C8.85652 0 10.637 0.737498 11.9497 2.05025C13.2625 3.36301 14 5.14348 14 7C14 8.85652 13.2625 10.637 11.9497 11.9497C10.637 13.2625 8.85652 14 7 14C5.14348 14 3.36301 13.2625 2.05025 11.9497C0.737498 10.637 0 8.85652 0 7ZM6.60053 9.996L10.6307 4.95787L9.90267 4.37547L6.46613 8.66973L4.032 6.6416L3.43467 7.3584L6.60053 9.996Z"
						fill="#108E56"
					/>
				</svg>
			);
		case 'Submitted':
			return (
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
					<circle cx="7" cy="7" r="6.5" stroke="#1A5BF8" />
					<path
						d="M2 7C2 7.98891 2.29324 8.95561 2.84265 9.77785C3.39206 10.6001 4.17295 11.241 5.08658 11.6194C6.00021 11.9978 7.00555 12.0969 7.97545 11.9039C8.94536 11.711 9.83627 11.2348 10.5355 10.5355C11.2348 9.83627 11.711 8.94536 11.9039 7.97545C12.0969 7.00555 11.9978 6.00021 11.6194 5.08658C11.241 4.17295 10.6001 3.39206 9.77785 2.84265C8.95561 2.29325 7.98891 2 7 2L7 7L2 7Z"
						fill="#1A5BF8"
					/>
				</svg>
			);
		default:
			return null;
	}
};

const DocumentCard = ({
	imageSrc,
	title,
	description,
	status,
	updatedAt,
	format,
	documentType,
	className = '',
	onDownload,
	onClick, // Add onClick prop here
	onOpenEditor, // Separate handler for "Open Editor" menu option
	documentId, // Add documentId prop for API calls
	isDownloading = false
}) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const menuRef = useRef(null);
	const [isImageLoaded, setIsImageLoaded] = useState(false);

	// Modal states
	const [showCopyConfirmation, setShowCopyConfirmation] = useState(false);
	const [showRenameModal, setShowRenameModal] = useState(false);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

	// Loading states
	const [isCopying, setIsCopying] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	// Map backend status to UI display label for this document type
	const displayStatus = toDisplayStatus(status, documentType);

	// Normalize format label for display
	const displayFormat =
		format === 'APA7th Edition' || format === 'APA 7th Edition'
			? 'APA 7th edition'
			: format;

	// Status dropdown state
	const [statusMenuOpen, setStatusMenuOpen] = useState(false);
	const [currentStatus, setCurrentStatus] = useState(displayStatus);
	const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
	const statusMenuRef = useRef(null);

	useOnClickOutside(menuRef, () => setMenuOpen(false));
	useOnClickOutside(statusMenuRef, () => setStatusMenuOpen(false));

	// Close on Esc
	useEffect(() => {
		const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);

	useEffect(() => {
		if (imageSrc) {
			const img = new Image();
			img.src = imageSrc;
			img.onload = () => setIsImageLoaded(true);
		} else {
			setIsImageLoaded(true);
		}
	}, [imageSrc]);

	// Keep local status in sync with prop (and legacy mapping)
	useEffect(() => {
		setCurrentStatus(toDisplayStatus(status, documentType));
	}, [status, documentType]);

	const cardDescription = getDocumentCardDescription({ description, documentType });

	const handleCopy = () => {
		setMenuOpen(false);
		setShowCopyConfirmation(true);
	};

	const handleRename = () => {
		setMenuOpen(false);
		setShowRenameModal(true);
	};

	const handleDownload = () => {
		setMenuOpen(false);
		if (onDownload) onDownload();
	};

	const handleDelete = () => {
		setMenuOpen(false);
		setShowDeleteConfirmation(true);
	};

	// API handlers
	const handleMakeCopyConfirm = async () => {
		if (!documentId) {
			Toast.error('Document ID is required');
			return;
		}

		try {
			setIsCopying(true);
			await DocumentService.duplicateDocument(documentId);
			Toast.success('Document copied successfully!');

			// Notify rest of the app that documents list has changed
			try {
				window.dispatchEvent(new Event('documents:updated'));
			} catch { }

			setShowCopyConfirmation(false);
		} catch (error) {
			const message = error?.response?.data?.message || error?.message || 'Failed to copy document';
			Toast.error(message);
		} finally {
			setIsCopying(false);
		}
	};

	const handleRenameConfirm = async (newTitle) => {
		if (!documentId) {
			Toast.error('Document ID is required');
			return;
		}

		try {
			await DocumentService.renameDocument(documentId, newTitle);
			Toast.success('Document renamed successfully!');

			// Notify rest of the app that documents list has changed
			try {
				window.dispatchEvent(new Event('documents:updated'));
			} catch { }

			setShowRenameModal(false);
		} catch (error) {
			const message =
				error?.response?.data?.message || error?.message || 'Failed to rename document';
			Toast.error(message);
			throw error; // Re-throw to let the modal handle it
		}
	};

	const handleDeleteConfirm = async () => {
		if (!documentId) {
			Toast.error('Document ID is required');
			return;
		}

		try {
			setIsDeleting(true);
			await DocumentService.deleteDocument(documentId);
			Toast.success('Document deleted successfully!');

			// Notify rest of the app that documents list has changed
			try {
				window.dispatchEvent(new Event('documents:updated'));
			} catch { }

			setShowDeleteConfirmation(false);
		} catch (error) {
			const message =
				error?.response?.data?.message || error?.message || 'Failed to delete document';
			Toast.error(message);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleStatusChange = async (newStatus) => {
		if (!documentId) {
			Toast.error('Document ID is required to update status');
			return;
		}
		if (newStatus === currentStatus || isUpdatingStatus) return;
		setIsUpdatingStatus(true);
		try {
			await DocumentService.updateDocumentStatus(documentId, newStatus, documentType);
			setCurrentStatus(newStatus);
			Toast.success('Status updated');
			try {
				window.dispatchEvent(new Event('documents:updated'));
			} catch { }
		} catch (error) {
			const message =
				error?.response?.data?.message || error?.message || 'Failed to update status';
			Toast.error(message);
		} finally {
			setIsUpdatingStatus(false);
			setStatusMenuOpen(false);
		}
	};

	return (
		<div
			className={`flex flex-col items-start gap-4 sm:gap-5 
					w-full p-3 md:p-4 lg:p-[15px] pb-4 md:pb-5 rounded-[14px] sm:rounded-[18px] 
					border border-[#F0F0F0] bg-white ${className}`}
		>
			{/* Thumbnail */}
			<div
				className="w-full h-32 sm:h-[150px] rounded-[14px] sm:rounded-[18px] 
					flex items-center justify-center overflow-hidden 
					bg-[#F8F2FC] cursor-pointer hover:opacity-95 transition"
				onClick={onClick}
			>
				{imageSrc ? (
					!isImageLoaded ? (
						<Skeleton
							height="100%"
							width="100%"
							baseColor="#E5E7EB"
							highlightColor="#F3F4F6"
							duration={1.2}
							className="rounded-[18px]"
						/>
					) : (
						<img
							src={imageSrc}
							alt="Document Preview"
							className="max-w-full max-h-full object-contain rounded-[18px]"
						/>
					)
				) : (
					<span className="text-gray-400 text-sm">No Preview</span>
				)}
			</div>

			{/* Title + menu */}
			<div className="flex justify-between items-start gap-2 w-full">
				<Tooltip content={title}>
					<h2
						className="text-dark-blue font-semibold break-words mb-3 text-lg leading-6 line-clamp-2 cursor-pointer hover:text-purple transition-colors"
						onClick={onClick}
					>
						{title}
					</h2>
				</Tooltip>

				{/* Kebab button + dropdown */}
				<div className="relative" ref={menuRef}>
					<button
						className="p-1 rounded hover:bg-gray-50 shrink-0"
						aria-label="More options"
						aria-haspopup="menu"
						aria-expanded={menuOpen}
						onClick={() => setMenuOpen((v) => !v)}
					>
						<svg width="4" height="14" viewBox="0 0 4 14" fill="none">
							<path d="M2 14C3.1 14 4 13.1 4 12S3.1 10 2 10 0 10.9 0 12s.9 2 2 2Z" fill="#B3B2BB" />
							<path d="M2 9c1.1 0 2-.9 2-2S3.1 5 2 5 0 5.9 0 7s.9 2 2 2Z" fill="#B3B2BB" />
							<path d="M2 4c1.1 0 2-.9 2-2S3.1 0 2 0 0 .9 0 2s.9 2 2 2Z" fill="#B3B2BB" />
						</svg>
					</button>

					{menuOpen && (
						<div
							role="menu"
							className="absolute right-0 z-20 mt-2 w-40 rounded-lg border border-[#F0F0F0] bg-white py-1"
						>
							<button
								role="menuitem"
								onClick={onOpenEditor || onClick}
								className="flex w-full items-center gap-2 px-3 py-2 text-sm text-dark-blue hover:bg-gray-50"
							>
								{/* edit/pencil icon */}
								<svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
									<path
										d="M13.5 3.5L16.5 6.5M4 16L5 12L13 4L16 7L8 15L4 16Z"
										stroke="#180A63"
										strokeWidth="1.2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
								Open Editor
							</button>
							<button
								role="menuitem"
								onClick={handleRename}
								className="flex w-full items-center gap-2 px-3 py-2 text-sm text-dark-blue hover:bg-gray-50"
							>
								{/* rename icon (text cursor) */}
								<svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
									<path
										d="M7 3H13M10 3V17M7 17H13"
										stroke="#180A63"
										strokeWidth="1.2"
										strokeLinecap="round"
									/>
								</svg>
								Rename
							</button>
							<button
								role="menuitem"
								onClick={handleDownload}
								disabled={isDownloading}
								className="flex w-full items-center gap-2 px-3 py-2 text-sm text-dark-blue hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
							>
								{/* download icon */}
								<svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
									<path
										d="M10 3V12M10 12L6.5 8.5M10 12L13.5 8.5M4 16H16"
										stroke="#180A63"
										strokeWidth="1.2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
								{isDownloading ? 'Downloading…' : 'Download'}
							</button>
							<button
								role="menuitem"
								onClick={handleCopy}
								className="flex w-full items-center gap-2 px-3 py-2 text-sm text-dark-blue hover:bg-gray-50"
							>
								{/* duplicate icon */}
								<svg width="16" height="16" fill="none" aria-hidden="true">
									<rect x="5" y="5" width="9" height="9" rx="2" stroke="#180A63" />
									<rect x="2" y="2" width="9" height="9" rx="2" stroke="#180A63" opacity="0.6" />
								</svg>
								Make Copy
							</button>
							<button
								role="menuitem"
								onClick={handleDelete}
								className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
							>
								{/* trash icon */}
								<svg width="16" height="16" fill="none" aria-hidden="true">
									<path
										d="M3 5h10l-.8 8.2A2 2 0 0 1 10.2 15H5.8A2 2 0 0 1 3.8 13.2L3 5Zm2-2h6l1 2H4l1-2Zm2 0V2a1 1 0 0 1 1-1h0a1 1 0 0 1 1 1v1"
										stroke="#DC2626"
										strokeWidth="1.2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
								Delete
							</button>
						</div>
					)}
				</div>
			</div>

			{cardDescription && (
				<p className="text-gray text-sm leading-[22px] flex-grow line-clamp-2 overflow-hidden text-ellipsis w-full">
					{cardDescription}
				</p>
			)}
			{/* Date + Time */}
			{updatedAt && (
				<div className="flex flex-row items-center gap-3 sm:gap-3 text-sm text-gray mb-2.5 flex-wrap">
					<div className="flex items-center gap-1 mb-1 sm:mb-0 w-[60px] flex-shrink-0">
						<span className="text-[11px] whitespace-nowrap">Last edited:</span>
					</div>

					<div className="flex items-center gap-1 mb-1 sm:mb-0 whitespace-nowrap flex-shrink-0">
						<CalendarIcon width={14} height={14} className="inline-block" />
						<span className="text-dark-blue text-[11px]">
							{new Date(updatedAt).toLocaleDateString(undefined, {
								year: 'numeric',
								month: 'short',
								day: 'numeric'
							})}
						</span>
					</div>

					<div className="flex items-center gap-1 whitespace-nowrap flex-shrink-0">
						<TimeIcon width={14} height={14} className="inline-block" />
						<span className="text-dark-blue text-[11px]">
							{new Date(updatedAt).toLocaleTimeString(undefined, {
								hour: 'numeric',
								minute: '2-digit'
							})}
						</span>
					</div>
				</div>
			)}

			{/* Status + Format + arrow */}
			<div className="flex justify-between items-start w-full">
				<div className="flex flex-wrap gap-2">
					{currentStatus && (
						<div className="relative" ref={statusMenuRef}>
							<Tooltip content={getStatusTooltip(currentStatus, documentType)}>
								<button
									type="button"
									className={`${PILL} ${
										STATUS_STYLES[getStatusStyleKey(currentStatus)] ||
										STATUS_STYLES[currentStatus] ||
										'bg-gray-100 text-gray-700 border border-gray-200'
									} cursor-pointer`}
									onClick={() => setStatusMenuOpen((open) => !open)}
									disabled={isUpdatingStatus}
								>
									<StatusIcon status={currentStatus} />
									{currentStatus}
								</button>
							</Tooltip>
							{statusMenuOpen && (
								<div className="absolute z-20 mt-1 w-56 rounded-lg border border-[#E5E7EB] bg-white shadow-lg">
									{getStatusMenuOptions(currentStatus, documentType).map(
										({ status: option, isCurrent, isEnabled, tooltip }) => (
										<button
											key={option}
											type="button"
											title={tooltip}
											onClick={() => isEnabled && !isCurrent && handleStatusChange(option)}
											className={`flex w-full flex-col items-start px-3 py-2 text-left ${
												isCurrent
													? 'bg-lightPurple text-dark-blue font-semibold'
													: isEnabled
														? 'text-gray hover:bg-gray-50'
														: 'text-gray/40 cursor-not-allowed'
											}`}
											disabled={isUpdatingStatus || !isEnabled || isCurrent}
										>
											<span className="text-xs">{option}</span>
											{tooltip ? (
												<span className="text-[10px] font-normal text-gray-500 leading-snug mt-0.5">
													{tooltip}
												</span>
											) : null}
										</button>
									)
									)}
								</div>
							)}
						</div>
					)}

					{displayFormat && (
						<span className={`${PILL} bg-[#F2F3F7] text-dark-blue border border-[#F2F3F7]`}>
							{displayFormat}
						</span>
					)}

					{documentType && (
						<span className={`${PILL} bg-[#F2F3F7] text-dark-blue border border-[#F2F3F7]`}>
							{getDocumentTypeDisplayName(documentType) || documentType}
						</span>
					)}
				</div>

				<Tooltip content="Open the editor to start writing and adding citations.">
					<button
						className="self-start mt-1 flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 transition shrink-0"
						aria-label="Open"
						onClick={onClick}
					>
						<svg width="18" height="12" fill="none" viewBox="0 0 18 12">
							<path
								d="M11.5 11L16.5 6M16.5 6L11.5 1M16.5 6H1.5"
								stroke="#180A63"
								strokeWidth="1.875"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
				</Tooltip>

			</div>

			{/* Copy Confirmation Modal */}
			<ConfirmationModal
				isOpen={showCopyConfirmation}
				onClose={() => setShowCopyConfirmation(false)}
				isLoading={isCopying}
				icon={
					<svg width="100" height="100" viewBox="0 0 100 100" fill="none">
						<rect
							x="25"
							y="25"
							width="45"
							height="45"
							rx="10"
							stroke="#180A63"
							strokeWidth="2"
							fill="none"
						/>
						<rect
							x="15"
							y="15"
							width="45"
							height="45"
							rx="10"
							stroke="#180A63"
							strokeWidth="2"
							fill="#F8F2FC"
							opacity="0.6"
						/>
					</svg>
				}
				heading="Make Copy"
				description="Are you sure you want to create a copy of this document? This will create a duplicate with the same content."
				primaryButtonText="Cancel"
				secondaryButtonText={isCopying ? 'Making Copy...' : 'Make Copy'}
				onPrimaryClick={() => setShowCopyConfirmation(false)}
				onSecondaryClick={handleMakeCopyConfirm}
			/>

			{/* Rename Modal */}
			<RenameDocumentModal
				isOpen={showRenameModal}
				onClose={() => setShowRenameModal(false)}
				currentTitle={title}
				onRename={handleRenameConfirm}
			/>

			{/* Delete Confirmation Modal */}
			<ConfirmationModal
				isOpen={showDeleteConfirmation}
				onClose={() => setShowDeleteConfirmation(false)}
				isLoading={isDeleting}
				icon={<img src={DeleteIcon} alt="Delete" />}
				heading="Delete Document"
				description="Are you sure you want to delete this document? This action cannot be undone and all content will be permanently lost."
				primaryButtonText="Cancel"
				secondaryButtonText={isDeleting ? 'Deleting...' : 'Delete'}
				onPrimaryClick={() => setShowDeleteConfirmation(false)}
				onSecondaryClick={handleDeleteConfirm}
			/>
		</div>
	);
};

export default DocumentCard;